import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import type { Commission, CommissionType, CommissionStatus } from '@/types/commission'

interface CommissionTableProps {
  commissions: Commission[]
  isLoading?: boolean
  showUserInfo?: boolean
  showDepartmentInfo?: boolean
}

// 佣金类型标签
const COMMISSION_TYPE_LABELS: Record<CommissionType, string> = {
  locker: '锁客',
  tracker: '跟进',
  promoter: '推广',
}

// 佣金类型颜色
const COMMISSION_TYPE_VARIANTS: Record<CommissionType, 'default' | 'secondary' | 'outline' | 'success' | 'warning' | 'info'> = {
  locker: 'success',
  tracker: 'info',
  promoter: 'warning',
}

// 佣金状态标签
const COMMISSION_STATUS_LABELS: Record<CommissionStatus, string> = {
  1: '正常',
  2: '已扣除',
}

// 佣金状态颜色
const COMMISSION_STATUS_VARIANTS: Record<CommissionStatus, 'default' | 'secondary' | 'outline' | 'success' | 'warning' | 'destructive'> = {
  1: 'success',
  2: 'destructive',
}

export function CommissionTable({
  commissions,
  isLoading,
  showUserInfo = false,
  showDepartmentInfo = false,
}: CommissionTableProps) {
  if (isLoading) {
    return (
      <div className="rounded-xl border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead><Skeleton className="h-4 w-24" /></TableHead>
              {showDepartmentInfo && <TableHead><Skeleton className="h-4 w-20" /></TableHead>}
              {showUserInfo && <TableHead><Skeleton className="h-4 w-20" /></TableHead>}
              <TableHead><Skeleton className="h-4 w-16" /></TableHead>
              <TableHead className="text-right"><Skeleton className="h-4 w-16 ml-auto" /></TableHead>
              <TableHead className="text-right"><Skeleton className="h-4 w-12 ml-auto" /></TableHead>
              <TableHead className="text-right"><Skeleton className="h-4 w-16 ml-auto" /></TableHead>
              <TableHead><Skeleton className="h-4 w-16" /></TableHead>
              <TableHead><Skeleton className="h-4 w-32" /></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {Array.from({ length: 5 }).map((_, i) => (
              <TableRow key={i}>
                <TableCell><Skeleton className="h-4 w-28" /></TableCell>
                {showDepartmentInfo && <TableCell><Skeleton className="h-4 w-20" /></TableCell>}
                {showUserInfo && <TableCell><Skeleton className="h-4 w-20" /></TableCell>}
                <TableCell><Skeleton className="h-6 w-16" /></TableCell>
                <TableCell><Skeleton className="h-4 w-16 ml-auto" /></TableCell>
                <TableCell><Skeleton className="h-4 w-12 ml-auto" /></TableCell>
                <TableCell><Skeleton className="h-4 w-16 ml-auto" /></TableCell>
                <TableCell><Skeleton className="h-6 w-16" /></TableCell>
                <TableCell><Skeleton className="h-4 w-32" /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    )
  }

  if (commissions.length === 0) {
    return (
      <div className="rounded-xl border bg-card py-12 text-center">
        <p className="text-muted-foreground">暂无佣金记录</p>
      </div>
    )
  }

  return (
    <div className="rounded-xl border bg-card">
      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead>订单号</TableHead>
            {showDepartmentInfo && <TableHead>部门</TableHead>}
            {showUserInfo && <TableHead>员工</TableHead>}
            <TableHead>类型</TableHead>
            <TableHead className="text-right">利润</TableHead>
            <TableHead className="text-right">比例</TableHead>
            <TableHead className="text-right">佣金</TableHead>
            <TableHead>状态</TableHead>
            <TableHead>创建时间</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {commissions.map((commission) => (
            <TableRow key={commission.id} className="group">
              <TableCell>
                <span className="font-mono text-sm">{commission.order_no}</span>
              </TableCell>
              {showDepartmentInfo && (
                <TableCell>
                  <span className="text-sm">{commission.department_name || '-'}</span>
                </TableCell>
              )}
              {showUserInfo && (
                <TableCell>
                  <span className="text-sm">{commission.user_nickname || '-'}</span>
                </TableCell>
              )}
              <TableCell>
                <Badge variant={COMMISSION_TYPE_VARIANTS[commission.commission_type]}>
                  {COMMISSION_TYPE_LABELS[commission.commission_type]}
                </Badge>
              </TableCell>
              <TableCell className="text-right">
                <span className="text-sm">¥{(commission.profit ?? 0).toFixed(2)}</span>
              </TableCell>
              <TableCell className="text-right">
                <span className="text-sm">{((commission.rate ?? 0) * 100).toFixed(1)}%</span>
              </TableCell>
              <TableCell className="text-right">
                <span className="font-semibold text-primary">
                  ¥{(commission.amount ?? 0).toFixed(2)}
                </span>
              </TableCell>
              <TableCell>
                <Badge variant={COMMISSION_STATUS_VARIANTS[commission.status]}>
                  {COMMISSION_STATUS_LABELS[commission.status]}
                </Badge>
              </TableCell>
              <TableCell>
                <span className="text-sm text-muted-foreground">{commission.created_at}</span>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}
